import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Star, Calendar, Clock, Shirt, Target, Activity } from 'lucide-react';
import { useChildDetail, usePlayerGearProfile, useUpdateGearProfile } from '@/hooks/use-parent-data';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { OverviewTab } from '@/components/parent/child-detail/overview-tab';
import { EvaluationsTab } from '@/components/parent/child-detail/evaluations-tab';
import { AttendanceTab } from '@/components/parent/child-detail/attendance-tab';
import { MakeupsSection } from '@/components/parent/child-detail/makeups-section';
import { SessionsTab } from '@/components/parent/child-detail/sessions-tab';
import { GearProfileCard } from '@/components/parent/gear-profile-card';
import { ChildGoals } from '@/components/parent/child-goals';
import { FitnessTab } from '@/components/parent/child-detail/fitness-tab';

type TabId = 'overview' | 'evaluations' | 'attendance' | 'sessions' | 'goals' | 'fitness' | 'locker';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'evaluations', label: 'Evaluations', icon: Star },
  { id: 'attendance', label: 'Attendance', icon: Calendar },
  { id: 'sessions', label: 'Sessions', icon: Clock },
  { id: 'goals', label: 'Goals', icon: Target },
  { id: 'fitness', label: 'Fitness', icon: Activity },
  { id: 'locker', label: 'Locker Room', icon: Shirt },
];

export default function ChildDetailPage() {
  const navigate = useNavigate();
  const { childId } = useParams<{ childId: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Guard against missing childId
  if (!childId) {
    return <div className="p-6">Invalid child ID</div>;
  }

  const { data, isLoading, error } = useChildDetail(childId);
  const { data: gearProfile, isLoading: gearLoading } = usePlayerGearProfile(childId);
  const updateGearProfile = useUpdateGearProfile(childId);

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Card>
          <CardContent className="p-6">
            <p className="text-red-600">Error loading child details. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        {isLoading ? (
          <Skeleton className="h-8 w-48" />
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold">
              {data?.profile.firstName[0]}{data?.profile.lastName[0]}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {data?.profile.firstName} {data?.profile.lastName}
              </h1>
              <div className="flex items-center gap-2">
                {data?.profile.level && (
                  <Badge variant="outline" className="capitalize">
                    {data.profile.level}
                  </Badge>
                )}
                {data?.profile.utr && (
                  <Badge variant="secondary" className="text-primary bg-primary/10">
                    UTR: {data.profile.utr}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 border-b-2 transition whitespace-nowrap',
                activeTab === tab.id
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && data && <OverviewTab data={data} />}
          {activeTab === 'evaluations' && data && <EvaluationsTab evaluations={data.evaluations} averages={data.stats.evalAverages} />}
          {activeTab === 'attendance' && data && (
            <>
              <MakeupsSection childId={childId} />
              <AttendanceTab attendance={data.attendance} stats={data.stats} />
            </>
          )}
          {activeTab === 'sessions' && data && <SessionsTab sessions={data.upcomingSessions} />}
          {activeTab === 'goals' && <ChildGoals childId={childId} />}
          {activeTab === 'fitness' && (
            <FitnessTab
              childId={childId}
              dateOfBirth={data?.profile.dateOfBirth ?? null}
            />
          )}
          {activeTab === 'locker' && (
            <GearProfileCard
              profile={gearProfile || null}
              isLoading={gearLoading}
              onUpdate={async (data) => {
                await updateGearProfile.mutateAsync(data);
              }}
              isUpdating={updateGearProfile.isPending}
            />
          )}
        </>
      )}
    </div>
  );
}
